/**
 * Guest Mode Utilities
 * Manages free analysis limit for non-authenticated users using localStorage
 * GDPR Compliant: No personal data stored, just usage count
 *
 * Supports dual frameworks:
 * - Current Framework: 2 free analyses for guests
 * - Advanced Framework: 1 free analysis for guests
 */

// Legacy key for backward compatibility
const GUEST_ANALYSIS_KEY = 'guestAnalysisCount';
const GUEST_ANALYSIS_TIMESTAMP = 'guestAnalysisTimestamp';

// Framework-specific keys
const GUEST_ANALYSIS_KEY_CURRENT = 'guestAnalysisCountCurrent';
const GUEST_ANALYSIS_KEY_ADVANCED = 'guestAnalysisCountAdvanced';

// Framework-specific limits for guests
const GUEST_LIMITS = {
  current: 2,   // 2 current framework analyses for guests
  advanced: 1   // 1 advanced framework analysis for guests
};

/**
 * Check if guest has remaining analyses for a specific framework
 * @param {string} frameworkType - 'current' or 'advanced'
 */
export function hasGuestAnalysisRemaining(frameworkType = 'current') {
  const count = getGuestAnalysisCount(frameworkType);
  const limit = GUEST_LIMITS[frameworkType] || GUEST_LIMITS.current;
  return count < limit;
}

/**
 * Get current guest analysis count for a specific framework
 * @param {string} frameworkType - 'current' or 'advanced'
 */
export function getGuestAnalysisCount(frameworkType = 'current') {
  let key;

  if (frameworkType === 'advanced') {
    key = GUEST_ANALYSIS_KEY_ADVANCED;
  } else {
    // For 'current', check new key first, fall back to legacy key
    const newCount = localStorage.getItem(GUEST_ANALYSIS_KEY_CURRENT);
    if (newCount !== null) {
      return parseInt(newCount, 10);
    }
    // Fall back to legacy key for backward compatibility
    key = GUEST_ANALYSIS_KEY;
  }

  const count = localStorage.getItem(key);
  return count ? parseInt(count, 10) : 0;
}

/**
 * Increment guest analysis count for a specific framework
 * @param {string} frameworkType - 'current' or 'advanced'
 */
export function incrementGuestAnalysis(frameworkType = 'current') {
  const currentCount = getGuestAnalysisCount(frameworkType);
  const newCount = currentCount + 1;

  if (frameworkType === 'advanced') {
    localStorage.setItem(GUEST_ANALYSIS_KEY_ADVANCED, newCount.toString());
  } else {
    // Update both legacy and new keys for 'current' framework
    localStorage.setItem(GUEST_ANALYSIS_KEY, newCount.toString());
    localStorage.setItem(GUEST_ANALYSIS_KEY_CURRENT, newCount.toString());
  }

  localStorage.setItem(GUEST_ANALYSIS_TIMESTAMP, new Date().toISOString());

  return newCount;
}

/**
 * Reset guest analysis count (for testing or user request)
 * @param {string} frameworkType - 'current', 'advanced', or 'all'
 */
export function resetGuestAnalysis(frameworkType = 'all') {
  if (frameworkType === 'all' || frameworkType === 'current') {
    localStorage.removeItem(GUEST_ANALYSIS_KEY);
    localStorage.removeItem(GUEST_ANALYSIS_KEY_CURRENT);
  }
  if (frameworkType === 'all' || frameworkType === 'advanced') {
    localStorage.removeItem(GUEST_ANALYSIS_KEY_ADVANCED);
  }
  if (frameworkType === 'all') {
    localStorage.removeItem(GUEST_ANALYSIS_TIMESTAMP);
  }
}

/**
 * Get guest limit for a specific framework
 * @param {string} frameworkType - 'current' or 'advanced'
 */
export function getGuestLimit(frameworkType = 'current') {
  return GUEST_LIMITS[frameworkType] || GUEST_LIMITS.current;
}

/**
 * Get remaining guest analyses for a specific framework
 * @param {string} frameworkType - 'current' or 'advanced'
 */
export function getGuestAnalysisRemaining(frameworkType = 'current') {
  const limit = GUEST_LIMITS[frameworkType] || GUEST_LIMITS.current;
  return Math.max(0, limit - getGuestAnalysisCount(frameworkType));
}

/**
 * Get quota status for all frameworks
 * Returns an object with quota info for both frameworks
 */
export function getAllGuestQuotaStatus() {
  return {
    current: {
      used: getGuestAnalysisCount('current'),
      limit: GUEST_LIMITS.current,
      remaining: getGuestAnalysisRemaining('current')
    },
    advanced: {
      used: getGuestAnalysisCount('advanced'),
      limit: GUEST_LIMITS.advanced,
      remaining: getGuestAnalysisRemaining('advanced')
    }
  };
}

/**
 * Get all guest limits
 */
export function getAllGuestLimits() {
  return { ...GUEST_LIMITS };
}
